
(function () {
  let activeKeydownHandler = null;
  // URLs already preloaded this session — never preload the same image twice.
  const preloadedUrls = new Set();

  const SWIPE_THRESHOLD_PX = 35;
  const SWIPE_AXIS_LOCK_PX = 10;
  const SWIPE_DIRECTION_RATIO = 1.2;

  const ZOOM_MIN = 1;
  const ZOOM_MAX = 4;
  const ZOOM_DOUBLE_TAP = 2.5;
  const DOUBLE_TAP_MS = 300;
  const DOUBLE_TAP_PX = 30;
  const PAN_ACTIVATE_PX = 8;
  const ZOOM_EPSILON = 1.01;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  /* AAS-5490: these overlays assign img.src in JS, so no <source> can be gated
   * on resolution and the density pick has to happen here. Liquid emits an
   * `_hd` twin carrying the hi-DPI quality; a DPR1 screen keeps the
   * full-quality URL, where harder compression would not be optically
   * absorbed. Resolved once at both entry points, so nothing downstream ever
   * sees more than one variant. */
  const IS_HIDPI = (() => {
    try {
      return !!(window.matchMedia
        && window.matchMedia('(min-resolution: 2dppx), (-webkit-min-device-pixel-ratio: 2)').matches);
    } catch (e) {
      return false;
    }
  })();
  const byDensity = (base, hd) => ((IS_HIDPI && hd) ? hd : base) || '';

  const getGalleryMediaData = (item) => {
    const type = (item.getAttribute('data-gallery-type') || 'image').trim();
    const dataSrc = byDensity(
      (item.getAttribute('data-gallery-src') || '').trim(),
      (item.getAttribute('data-gallery-src-hd') || '').trim(),
    );
    const poster = byDensity(
      (item.getAttribute('data-gallery-poster') || '').trim(),
      (item.getAttribute('data-gallery-poster-hd') || '').trim(),
    );
    const alt = (item.getAttribute('data-gallery-alt') || '').trim();
    const caption = (item.getAttribute('data-gallery-caption') || '').trim();
    const loop = item.getAttribute('data-gallery-loop') === 'true';

    return {
      type,
      src: dataSrc,
      poster,
      alt,
      caption,
      loop,
    };
  };

  const buildPreviewMedia = (media) => {
    if (!media) return null;
    if (media.type === 'image') return media;
    if (media.poster) {
      return {
        type: 'image',
        src: media.poster,
        poster: '',
        alt: media.alt,
        caption: media.caption,
        loop: false,
      };
    }
    return media;
  };

  const createMediaElement = (media, index, isPrimary, zoomable) => {
    const fallbackAlt = `Media ${index + 1}`;
    const alt = media.alt || fallbackAlt;

    if (media.type === 'video' && media.src) {
      const video = document.createElement('video');
      video.className = 'armada-gallery__img armada-gallery__media-video';
      video.src = media.src;
      video.controls = isPrimary;
      video.autoplay = isPrimary;
      video.muted = true;
      // Half volume for the moment the shopper unmutes — the theme-wide
      // default, matching armada-video.js and deferred-media.
      video.volume = 0.5;
      video.loop = media.loop;
      video.playsInline = true;
      video.preload = 'metadata';
      if (media.poster) {
        video.poster = media.poster;
      }
      video.setAttribute('aria-label', alt);
      return video;
    }

    if (media.type === 'external_video' && media.src) {
      if (!isPrimary && media.poster) {
        const preview = document.createElement('img');
        preview.className = 'armada-gallery__img';
        preview.src = media.poster;
        preview.alt = alt;
        return preview;
      }

      const iframe = document.createElement('iframe');
      iframe.className = 'armada-gallery__img armada-gallery__media-iframe';
      iframe.src = media.src;
      iframe.setAttribute('title', alt);
      iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
      iframe.setAttribute('allowfullscreen', 'true');
      iframe.setAttribute('loading', 'eager');
      return iframe;
    }

    const image = document.createElement('img');
    image.className = zoomable
      ? 'armada-gallery__img armada-gallery__img--zoomable'
      : 'armada-gallery__img';
    image.decoding = 'async';
    image.draggable = false;
    // The current (primary) image is what the viewer is waiting on — fetch it
    // ahead of the secondary preview and anything else still loading.
    image.setAttribute('fetchpriority', isPrimary ? 'high' : 'low');
    image.src = media.src || media.poster || '';
    image.alt = alt;
    return image;
  };

  const renderMedia = (holder, media, index, isPrimary, zoom) => {
    holder.innerHTML = '';
    if (!media || (!media.src && !media.poster)) {
      zoom?.attach(null);
      return;
    }

    const isZoomableImage =
      Boolean(zoom) && isPrimary && (media.type === 'image' || (!media.type && media.src));
    const mediaNode = createMediaElement(media, index, isPrimary, isZoomableImage);
    holder.appendChild(mediaNode);

    zoom?.attach(isZoomableImage ? mediaNode : null);

    if (isPrimary && mediaNode.tagName === 'VIDEO') {
      const playPromise = mediaNode.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }
  };

  const LABEL_MARKUP = `
    <div class="armada-gallery__label-wrap" hidden>
      <div class="armada-gallery__label-card"><p class="armada-gallery__label-text"></p></div>
    </div>`;

  const renderCaption = (slot, media) => {
    const wrap = slot?.querySelector('.armada-gallery__label-wrap');
    const text = wrap?.querySelector('.armada-gallery__label-text');
    if (!wrap || !text) return;

    const caption = (media?.caption || '').trim();
    text.textContent = caption;
    wrap.hidden = !caption;
  };

  // Optional per-slide product card (Lookbook strips). Built via DOM so the
  // product title/price are inserted as text, never parsed as HTML.
  const renderProductCard = (overlay, product) => {
    const holder = overlay.querySelector('.armada-gallery__product');
    if (!holder) return;
    holder.innerHTML = '';
    if (!product || !product.title) {
      holder.style.display = 'none';
      return;
    }
    holder.style.display = '';

    const card = document.createElement('a');
    card.className = 'armada-gallery__product-card';
    card.href = product.url || '#';
    card.setAttribute('aria-label', product.title);

    if (product.image) {
      const imgWrap = document.createElement('span');
      imgWrap.className = 'armada-gallery__product-img';
      const img = document.createElement('img');
      img.src = product.image;
      img.alt = '';
      img.loading = 'lazy';
      imgWrap.appendChild(img);
      card.appendChild(imgWrap);
    }

    const info = document.createElement('span');
    info.className = 'armada-gallery__product-info';

    const title = document.createElement('span');
    title.className = 'armada-gallery__product-title';
    title.textContent = product.title;
    info.appendChild(title);

    if (product.price) {
      const price = document.createElement('span');
      price.className = 'armada-gallery__product-price';
      price.textContent = product.price;
      info.appendChild(price);
    }

    card.appendChild(info);
    holder.appendChild(card);
  };

  function createOverlay(total) {
    const div = document.createElement('div');
    div.id = 'armada-gallery';
    div.className = 'armada-gallery';
    div.setAttribute('aria-hidden', 'true');
    div.setAttribute('role', 'dialog');
    div.setAttribute('aria-modal', 'true');

    div.innerHTML =
      '<div class="armada-gallery__inner">' +
        '<div class="armada-gallery__images">' +
          '<div class="armada-gallery__image-slot armada-gallery__image-slot--main">' +
            '<div class="armada-gallery__media-holder armada-gallery__media-holder--main"></div>' +
            LABEL_MARKUP +
            '<div class="armada-gallery__product" style="display:none"></div>' +
          '</div>' +
          '<div class="armada-gallery__image-slot armada-gallery__image-slot--secondary">' +
            '<div class="armada-gallery__media-holder armada-gallery__media-holder--secondary"></div>' +
            LABEL_MARKUP +
          '</div>' +
        '</div>' +
        '<button class="armada-gallery__nav armada-gallery__nav--prev" aria-label="Previous media">' +
          '<div class="armada-gallery__nav-btn">' +
            '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="24" height="24">' +
              '<path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />' +
            '</svg>' +
          '</div>' +
        '</button>' +
        '<button class="armada-gallery__nav armada-gallery__nav--next" aria-label="Next media">' +
          '<div class="armada-gallery__nav-btn">' +
            '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="24" height="24">' +
              '<path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />' +
            '</svg>' +
          '</div>' +
        '</button>' +
        '<button class="armada-gallery__close" aria-label="Close gallery">' +
          '<div class="armada-gallery__close-btn">' +
            '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="24" height="24">' +
              '<path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />' +
            '</svg>' +
          '</div>' +
        '</button>' +
        '<div class="armada-gallery__counter-wrap">' +
          '<div class="armada-gallery__counter">' +
            '<span class="armada-gallery__counter-text">1/' + total + '</span>' +
          '</div>' +
        '</div>' +
      '</div>';

    return div;
  }

  function openGallery(overlay, galleryMedia, index, zoom) {
    overlay.setAttribute('aria-hidden', 'false');
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    updateGallery(overlay, galleryMedia, index, zoom);
  }

  function closeGallery(overlay, zoom) {
    zoom?.reset();
    overlay.setAttribute('aria-hidden', 'true');
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    overlay.querySelectorAll('video').forEach((video) => {
      video.pause();
    });
    overlay.querySelectorAll('.armada-gallery__media-iframe').forEach((iframe) => {
      iframe.src = 'about:blank';
    });

    const mainHolder = overlay.querySelector('.armada-gallery__media-holder--main');
    const secondaryHolder = overlay.querySelector('.armada-gallery__media-holder--secondary');
    if (mainHolder) mainHolder.innerHTML = '';
    if (secondaryHolder) secondaryHolder.innerHTML = '';
    zoom?.attach(null);
    renderProductCard(overlay, null);
  }

  function updateGallery(overlay, galleryMedia, index, zoom) {
    zoom?.reset();

    const total = galleryMedia.length;
    const mainHolder = overlay.querySelector('.armada-gallery__media-holder--main');
    const secondarySlot = overlay.querySelector('.armada-gallery__image-slot--secondary');
    const secondaryHolder = overlay.querySelector('.armada-gallery__media-holder--secondary');
    const counter = overlay.querySelector('.armada-gallery__counter-text');
    const prevBtn = overlay.querySelector('.armada-gallery__nav--prev');
    const nextBtn = overlay.querySelector('.armada-gallery__nav--next');
    const imagesContainer = overlay.querySelector('.armada-gallery__images');
    const mainSlot = overlay.querySelector('.armada-gallery__image-slot--main');

    renderMedia(mainHolder, galleryMedia[index], index, true, zoom);
    renderCaption(mainSlot, galleryMedia[index]);
    renderProductCard(overlay, galleryMedia[index]?.product ?? null);

    if (index + 1 < total) {
      const secondaryMedia = buildPreviewMedia(galleryMedia[index + 1]);
      renderMedia(secondaryHolder, secondaryMedia, index + 1, false, null);
      renderCaption(secondarySlot, secondaryMedia);
      secondarySlot.style.display = '';
      imagesContainer.classList.remove('armada-gallery__images--single');
      mainSlot.classList.remove('armada-gallery__image-slot--centered');
    } else {
      secondaryHolder.innerHTML = '';
      renderCaption(secondarySlot, null);
      secondarySlot.style.display = 'none';
      imagesContainer.classList.add('armada-gallery__images--single');
      mainSlot.classList.add('armada-gallery__image-slot--centered');
    }

    counter.textContent = `${index + 1}/${total}`;

    // Warm the cache for the adjacent slides so navigating prev/next is
    // instant. Only images are prefetched (videos/embeds load on demand);
    // lookbook items carry no `type` but are always images.
    const preloadNeighbor = (m) => {
      if (!m) return;
      const isImage = m.type === 'image' || (!m.type && m.src);
      const url = isImage ? m.src : m.poster;
      if (url && !preloadedUrls.has(url)) {
        preloadedUrls.add(url);
        const pre = new Image();
        pre.decoding = 'async';
        pre.src = url;
      }
    };
    preloadNeighbor(galleryMedia[index - 1]);
    preloadNeighbor(galleryMedia[index + 1]);

    if (total <= 1) {
      prevBtn.style.display = 'none';
      nextBtn.style.display = 'none';
      prevBtn.classList.add('is-inactive');
      nextBtn.classList.add('is-inactive');
      return;
    }

    prevBtn.style.display = '';
    nextBtn.style.display = '';

    if (index === 0) {
      prevBtn.classList.add('is-inactive');
    } else {
      prevBtn.classList.remove('is-inactive');
    }

    if (index >= total - 1) {
      nextBtn.classList.add('is-inactive');
    } else {
      nextBtn.classList.remove('is-inactive');
    }
  }

  // Mobile-only pinch / pan / double-tap zoom for the primary image slot.
  function createZoomController(slotEl, overlay) {
    let imageEl = null;
    let scale = ZOOM_MIN;
    let tx = 0;
    let ty = 0;

    let pinching = false;
    let panning = false;
    let panCandidate = false;
    let startDist = 0;
    let startScale = 1;
    let startTx = 0;
    let startTy = 0;
    let pinchOriginX = 0;
    let pinchOriginY = 0;
    let panStartX = 0;
    let panStartY = 0;

    let lastTapTime = 0;
    let lastTapX = 0;
    let lastTapY = 0;
    let suppressClickUntil = 0;

    const getDistance = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

    const getMidpoint = (a, b) => ({
      x: (a.clientX + b.clientX) / 2,
      y: (a.clientY + b.clientY) / 2,
    });

    const slotCenter = () => {
      const rect = slotEl.getBoundingClientRect();
      return {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
    };

    const applyTransform = (animate) => {
      if (!imageEl) return;
      if (animate) {
        imageEl.classList.remove('is-gesturing');
      } else {
        imageEl.classList.add('is-gesturing');
      }
      imageEl.style.transform = `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`;
    };

    const clampPan = () => {
      if (!imageEl || !slotEl) return;
      const slotRect = slotEl.getBoundingClientRect();
      const { offsetWidth: baseW, offsetHeight: baseH } = imageEl;
      const scaledW = baseW * scale;
      const scaledH = baseH * scale;
      const maxTx = Math.max(0, (scaledW - slotRect.width) / 2);
      const maxTy = Math.max(0, (scaledH - slotRect.height) / 2);
      tx = clamp(tx, -maxTx, maxTx);
      ty = clamp(ty, -maxTy, maxTy);
    };

    // Keep the viewport point (anchorX, anchorY) fixed while changing scale.
    // With transform-origin: center and transform order scale-then-translate,
    // screenOffset = contentOffset * scale + translate.
    const zoomAround = (newScale, anchorX, anchorY) => {
      const center = slotCenter();
      const offsetX = anchorX - center.x;
      const offsetY = anchorY - center.y;
      const contentX = (offsetX - tx) / scale;
      const contentY = (offsetY - ty) / scale;
      scale = clamp(newScale, ZOOM_MIN, ZOOM_MAX);
      tx = offsetX - contentX * scale;
      ty = offsetY - contentY * scale;
      clampPan();
    };

    const reset = () => {
      scale = ZOOM_MIN;
      tx = 0;
      ty = 0;
      pinching = false;
      panning = false;
      panCandidate = false;
      if (imageEl) {
        imageEl.classList.remove('is-gesturing');
        imageEl.style.transform = '';
      }
    };

    const attach = (el) => {
      reset();
      imageEl = el;
    };

    const isZoomed = () => scale > ZOOM_EPSILON;

    const beginPan = (touch) => {
      panning = true;
      panCandidate = false;
      panStartX = touch.clientX;
      panStartY = touch.clientY;
      startTx = tx;
      startTy = ty;
      if (imageEl) imageEl.classList.add('is-gesturing');
    };

    const onStart = (event) => {
      if (!imageEl) return;

      if (event.touches.length === 2) {
        event.preventDefault();
        pinching = true;
        panning = false;
        panCandidate = false;
        const [a, b] = event.touches;
        startDist = getDistance(a, b) || 1;
        startScale = scale;
        startTx = tx;
        startTy = ty;
        const mid = getMidpoint(a, b);
        const center = slotCenter();
        pinchOriginX = mid.x - center.x;
        pinchOriginY = mid.y - center.y;
        imageEl.classList.add('is-gesturing');
        return;
      }

      // Defer pan until the finger actually moves, so a quick double-tap
      // while zoomed can still zoom back out.
      if (event.touches.length === 1 && isZoomed()) {
        pinching = false;
        panning = false;
        panCandidate = true;
        const touch = event.touches[0];
        panStartX = touch.clientX;
        panStartY = touch.clientY;
        startTx = tx;
        startTy = ty;
      }
    };

    const onMove = (event) => {
      if (!imageEl) return;

      if (pinching && event.touches.length >= 2) {
        event.preventDefault();
        const [a, b] = event.touches;
        const dist = getDistance(a, b) || 1;
        const nextScale = clamp(startScale * (dist / startDist), ZOOM_MIN, ZOOM_MAX);
        const mid = getMidpoint(a, b);
        const center = slotCenter();
        const offsetX = mid.x - center.x;
        const offsetY = mid.y - center.y;
        // Content under the original pinch midpoint, then re-anchor to the live midpoint.
        const contentX = (pinchOriginX - startTx) / startScale;
        const contentY = (pinchOriginY - startTy) / startScale;
        scale = nextScale;
        tx = offsetX - contentX * scale;
        ty = offsetY - contentY * scale;
        clampPan();
        applyTransform(false);
        return;
      }

      if (event.touches.length === 1 && isZoomed()) {
        const touch = event.touches[0];
        if (panCandidate && !panning) {
          const moved = Math.hypot(touch.clientX - panStartX, touch.clientY - panStartY);
          if (moved >= PAN_ACTIVATE_PX) {
            beginPan(touch);
          }
        }
        if (panning) {
          event.preventDefault();
          tx = startTx + (touch.clientX - panStartX);
          ty = startTy + (touch.clientY - panStartY);
          clampPan();
          applyTransform(false);
        }
      }
    };

    const settle = () => {
      if (scale <= ZOOM_EPSILON) {
        reset();
        return;
      }
      clampPan();
      applyTransform(true);
    };

    const handleDoubleTap = (touch) => {
      const now = Date.now();
      const dt = now - lastTapTime;
      const dx = touch.clientX - lastTapX;
      const dy = touch.clientY - lastTapY;
      const isDoubleTap =
        dt > 0 &&
        dt < DOUBLE_TAP_MS &&
        Math.hypot(dx, dy) < DOUBLE_TAP_PX;

      if (isDoubleTap) {
        suppressClickUntil = now + DOUBLE_TAP_MS;
        lastTapTime = 0;
        if (isZoomed()) {
          reset();
        } else {
          zoomAround(ZOOM_DOUBLE_TAP, touch.clientX, touch.clientY);
          applyTransform(true);
        }
        return true;
      }

      lastTapTime = now;
      lastTapX = touch.clientX;
      lastTapY = touch.clientY;
      return false;
    };

    const onEnd = (event) => {
      if (!imageEl) return;

      if (pinching) {
        // Still have two fingers? Keep pinching. Otherwise settle.
        if (event.touches.length >= 2) return;
        pinching = false;
        if (event.touches.length === 1 && isZoomed()) {
          // Transition into pan with the remaining finger.
          beginPan(event.touches[0]);
          return;
        }
        panning = false;
        panCandidate = false;
        settle();
        return;
      }

      if (panning) {
        panning = false;
        panCandidate = false;
        settle();
        return;
      }

      // Tap (possibly double-tap) — no pan was activated.
      if (panCandidate) {
        panCandidate = false;
      }

      if (event.changedTouches?.length === 1 && event.touches.length === 0) {
        const touch = event.changedTouches[0];
        if (handleDoubleTap(touch)) {
          event.preventDefault();
        }
      }
    };

    const onCancel = () => {
      pinching = false;
      panning = false;
      panCandidate = false;
      settle();
    };

    // Block iOS Safari's proprietary gesture events that can still page-zoom.
    const blockGesture = (event) => {
      event.preventDefault();
    };

    slotEl.addEventListener('touchstart', onStart, { passive: false });
    slotEl.addEventListener('touchmove', onMove, { passive: false });
    slotEl.addEventListener('touchend', onEnd, { passive: false });
    slotEl.addEventListener('touchcancel', onCancel, { passive: true });

    if (overlay) {
      overlay.addEventListener('gesturestart', blockGesture, { passive: false });
      overlay.addEventListener('gesturechange', blockGesture, { passive: false });
      overlay.addEventListener('gestureend', blockGesture, { passive: false });
      // Avoid the synthetic click that follows a double-tap zoom.
      overlay.addEventListener(
        'click',
        (event) => {
          if (Date.now() < suppressClickUntil) {
            event.preventDefault();
            event.stopPropagation();
          }
        },
        true
      );
    }

    return { attach, reset, isZoomed };
  }

  // Builds the overlay, wires its controls, and returns an opener. Shared by
  // the PDP auto-init and the public window.ArmadaGallery.open() entry point.
  function bindGallerySwipe(target, onNavigate, isZoomed) {
    if (!target || (!('ontouchstart' in window) && !navigator.maxTouchPoints)) return;

    let startX = 0;
    let startY = 0;
    let tracking = false;
    let lockedAxis = null;

    const onStart = (event) => {
      if (typeof isZoomed === 'function' && isZoomed()) return;
      if (event.touches?.length > 1) return;
      const touch = event.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      tracking = true;
      lockedAxis = null;
    };

    const onMove = (event) => {
      if (!tracking || event.touches?.length !== 1) return;
      if (typeof isZoomed === 'function' && isZoomed()) {
        tracking = false;
        return;
      }

      const touch = event.touches[0];
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;

      if (!lockedAxis && (Math.abs(dx) > SWIPE_AXIS_LOCK_PX || Math.abs(dy) > SWIPE_AXIS_LOCK_PX)) {
        lockedAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }

      if (lockedAxis === 'x') {
        event.preventDefault();
      }
    };

    const onEnd = (event) => {
      if (!tracking) return;
      tracking = false;
      if (typeof isZoomed === 'function' && isZoomed()) return;

      const touch = event.changedTouches?.[0];
      if (!touch) return;

      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;

      if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
      if (Math.abs(dx) < Math.abs(dy) * SWIPE_DIRECTION_RATIO) return;

      onNavigate(dx < 0 ? 1 : -1);
    };

    const onCancel = () => {
      tracking = false;
    };

    target.addEventListener('touchstart', onStart, { passive: true });
    target.addEventListener('touchmove', onMove, { passive: false });
    target.addEventListener('touchend', onEnd, { passive: true });
    target.addEventListener('touchcancel', onCancel, { passive: true });
  }

  function setupOverlay(galleryMedia) {
    const existingOverlay = document.getElementById('armada-gallery');
    if (existingOverlay) existingOverlay.remove();

    const overlay = createOverlay(galleryMedia.length);
    document.body.appendChild(overlay);

    let currentIndex = 0;

    const enableZoom = window.matchMedia('(max-width: 1023px)').matches;
    const mainSlot = overlay.querySelector('.armada-gallery__image-slot--main');
    const zoom = enableZoom && mainSlot ? createZoomController(mainSlot, overlay) : null;

    const navigateBy = (direction) => {
      if (zoom?.isZoomed()) return;
      const nextIndex = currentIndex + direction;
      if (nextIndex < 0 || nextIndex >= galleryMedia.length) return;
      currentIndex = nextIndex;
      updateGallery(overlay, galleryMedia, currentIndex, zoom);
    };

    overlay.querySelector('.armada-gallery__close').addEventListener('click', () => {
      closeGallery(overlay, zoom);
    });

    const prevBtn = overlay.querySelector('.armada-gallery__nav--prev');
    prevBtn.addEventListener('click', () => {
      navigateBy(-1);
    });

    const nextBtn = overlay.querySelector('.armada-gallery__nav--next');
    nextBtn.addEventListener('click', () => {
      navigateBy(1);
    });

    if (activeKeydownHandler) {
      document.removeEventListener('keydown', activeKeydownHandler);
    }

    // Escape resets zoom first when zoomed, then closes — clearer for keyboard users.
    activeKeydownHandler = (event) => {
      if (!overlay.classList.contains('is-open')) return;
      if (event.key === 'Escape') {
        if (zoom?.isZoomed()) {
          zoom.reset();
          return;
        }
        closeGallery(overlay, zoom);
      } else if (event.key === 'ArrowLeft' && currentIndex > 0) {
        navigateBy(-1);
      } else if (event.key === 'ArrowRight' && currentIndex < galleryMedia.length - 1) {
        navigateBy(1);
      }
    };

    document.addEventListener('keydown', activeKeydownHandler);

    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) closeGallery(overlay, zoom);
    });

    const swipeTarget = overlay.querySelector('.armada-gallery__images');
    bindGallerySwipe(swipeTarget, navigateBy, zoom ? () => zoom.isZoomed() : null);

    return {
      overlay,
      open(index) {
        currentIndex = index;
        openGallery(overlay, galleryMedia, index, zoom);
      },
    };
  }

  // AAS-5395: when the PIM gallery is grouped by colour, the overlay must
  // only walk the selected colour — not every colourway still in the DOM.
  const isLightboxEnabled = (item) =>
    item.getAttribute('data-gallery-lightbox-enabled') === 'true';

  const getSelectedColorGroup = (gallery) => {
    const carousel = gallery.closest('gallery-carousel');
    if (!carousel || carousel.dataset.mediaGrouping !== 'true') return null;

    const root = gallery.closest('.product') || document;
    const checked = root.querySelector('.variant-picker__color input[type="radio"]:checked');
    const selected = (checked?.value || carousel.dataset.filterSelected || '').trim();
    return selected || null;
  };

  const isSlideHiddenByColorFilter = (slide) => {
    if (!slide) return false;
    return slide.classList.contains('hidden-by-filter') || slide.style.display === 'none';
  };

  const collectGalleryEntries = (gallery) => {
    const selectedGroup = getSelectedColorGroup(gallery);
    const entries = [];

    for (const item of gallery.querySelectorAll('.product-media-container')) {
      if (!isLightboxEnabled(item)) continue;

      const slide = item.closest('.splide__slide');
      if (selectedGroup && slide?.dataset.mediaGroup && slide.dataset.mediaGroup !== selectedGroup) continue;
      if (isSlideHiddenByColorFilter(slide)) continue;

      const media = getGalleryMediaData(item);
      if (!media.src && !media.poster) continue;

      entries.push({ item, media });
    }

    return entries;
  };

  const openFilteredGallery = (gallery, clickedItem) => {
    const entries = collectGalleryEntries(gallery);
    if (!entries.length) return;

    const matchedIndex = entries.findIndex((entry) => entry.item === clickedItem);
    const galleryMedia = entries.map((entry) => entry.media);

    const { open } = setupOverlay(galleryMedia);
    open(matchedIndex >= 0 ? matchedIndex : 0);
  };

  // PDP: bind the product media gallery to the overlay. Media list is built
  // at click time so colour changes (and mobile Splide clones) stay in sync.
  const initGallery = () => {
    document.querySelectorAll('.splide__gallery').forEach((gallery) => {
      if (gallery.dataset.armadaGalleryBound === 'true') return;
      gallery.dataset.armadaGalleryBound = 'true';

      gallery.addEventListener('click', (event) => {
        const item = event.target.closest('.product-media-container');
        if (!item || !gallery.contains(item) || !isLightboxEnabled(item)) return;

        event.preventDefault();
        openFilteredGallery(gallery, item);
      });
    });
  };

  // Public entry point for callers that already have a media list (e.g. the
  // Lookbook strips). media items:
  //   { type?, src, src_hd?, poster?, poster_hd?, alt?, caption?, loop?, product? }
  // where product (optional) = { url, title, price, image }. The `_hd` twins
  // are the AAS-5490 hi-DPI variants and are optional — without them the base
  // URL answers every density.
  window.ArmadaGallery = {
    open(galleryMedia, startIndex) {
      if (!Array.isArray(galleryMedia) || !galleryMedia.length) return;
      const normalized = galleryMedia.map((media) => ({
        type: media.type || 'image',
        src: byDensity(media.src, media.src_hd),
        poster: byDensity(media.poster, media.poster_hd),
        alt: media.alt || '',
        caption: media.caption || '',
        loop: media.loop === true,
        product: media.product || null,
      }));
      const index = clamp(parseInt(startIndex, 10) || 0, 0, normalized.length - 1);
      const { open } = setupOverlay(normalized);
      open(index);
    },
  };

  document.addEventListener('DOMContentLoaded', () => {
    initGallery();
    document.addEventListener('shopify:section:load', () => {
      initGallery();
    });
  });
})();
